import axios from "axios";

import { ApiEndpoints } from "../apiEndpoints";
import { requestApi } from "../apiService";
import {
  CreateInviteRequest,
  CreateInviteResponse,
  CreateMeetingInviteRequest,
  RedeemInviteRequest,
  RedeemInviteResponse,
} from "../types/umbra/meet";

/**
 * BARE axios client for the PUBLIC redeem endpoint — deliberately separate from
 * the shared `apiService` client. It has NO request interceptor (so it never
 * attaches the logged-in user's `Authorization: Bearer` header to a public
 * endpoint) and NO 401 refresh interceptor (so a redeem failure can never trip
 * `resetAuthState()` and log the user out). A logged-in Orion user opening a
 * guest link must redeem exactly like an anonymous visitor.
 */
const publicClient = axios.create({
  headers: { "Content-Type": "application/json" },
});

/**
 * Create a guest invite for a GROUP chat (ow2-chat-be #218).
 * Requires the caller to be an owner/moderator of the chat — the backend
 * enforces this and returns a generic 403 otherwise (no existence leak).
 *
 * The returned `inviteCode` is a SECRET returned exactly once and is never
 * retrievable again — it must NOT be logged. The caller embeds it in a
 * shareable guest link.
 */
export async function createGuestInvite(
  request: CreateInviteRequest,
): Promise<CreateInviteResponse> {
  const response = await requestApi({
    url: ApiEndpoints.MEET_INVITES,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: request,
  });
  return response.data as CreateInviteResponse;
}

/**
 * Create a guest invite for a STANDALONE MEETING (Product B guest join).
 * Requires the caller to be the meeting owner — the backend enforces this
 * and returns a generic 404 otherwise (no existence leak; same response as
 * an unknown/non-usable meeting).
 *
 * Uses the shared AUTHED `requestApi` (Bearer + 401-refresh interceptor) —
 * this is an owner action, unlike `redeemGuestInvite` below.
 *
 * The returned `inviteCode` is a SECRET returned exactly once and is never
 * retrievable again — it must NOT be logged. The caller embeds it in a
 * shareable guest link via `buildGuestInviteLink`.
 */
export async function createMeetingGuestInvite(
  request: CreateMeetingInviteRequest,
): Promise<CreateInviteResponse> {
  const response = await requestApi({
    url: ApiEndpoints.MEET_MEETINGS_INVITES,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: request,
  });
  return response.data as CreateInviteResponse;
}

/**
 * Redeem a guest invite code (public / unauthenticated) and mint a guest token.
 * The code travels in the POST body (never the URL) so it stays out of access
 * logs. Any failure reason (invalid / expired / revoked / exhausted) collapses
 * to ONE generic backend error — callers must treat every failure identically
 * and never surface which reason applied.
 *
 * Uses the BARE `publicClient` (no auth header, no refresh interceptor) so a
 * logged-in user opening a guest link neither leaks their Bearer token to the
 * public endpoint nor gets logged out by the 401→refresh→reset path.
 */
export async function redeemGuestInvite(
  request: RedeemInviteRequest,
): Promise<RedeemInviteResponse> {
  const response = await publicClient.post<RedeemInviteResponse>(
    ApiEndpoints.MEET_INVITE_REDEEM,
    request,
  );
  return response.data;
}
